import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";

const AppConfigTable = () => {
  const [platform, setPlatform] = useState("all");
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState(null);

  // Fetch config data based on platform
  const fetchConfig = async (selectedPlatform) => {
    setLoading(true);
    try {
      const url =
        selectedPlatform === "all"
          ? "/appdashboard/update-config/"
          : `/appdashboard/update-config/?platform=${selectedPlatform}`;
      const response = await axiosConfig({
        method: "get",
        url: url,
      });
      const data = response?.data?.data || response?.data;
      setConfigData(data);
    } catch (error) {
      const errMsg =
        error?.response?.data?.response ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch config.";
      toast.error(errMsg);
      setConfigData(null);
    } finally {
      setLoading(false);
    }
  };


  // Fetch data on mount and when platform changes
  useEffect(() => {
    fetchConfig(platform);
  }, [platform]);

  const handlePlatformChange = (e) => {
    setPlatform(e.target.value);
  };

  // Table styles
  const thStyle = {
    padding: "12px",
    textAlign: "left",
    fontWeight: 600,
    borderBottom: "1px solid #ddd",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid #eee",
    maxWidth: 200,
    wordBreak: "break-word",
  };

  // Format date - only show date part (no time)
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Current App Configuration
      </Typography>

      {/* Platform Dropdown */}
      <FormControl sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Platform</InputLabel>
        <Select
          value={platform}
          label="Platform"
          onChange={handlePlatformChange}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="android">Android</MenuItem>
          <MenuItem value="ios">iOS</MenuItem>
        </Select>
      </FormControl>

      {/* Config Data Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </Box>
      ) : configData ? (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #ddd",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f5f5f5" }}>
              <th style={thStyle}>Platform</th>
              <th style={thStyle}>Latest Version</th>
              <th style={thStyle}>Min Supported Version</th>
              <th style={thStyle}>Force Update</th>
              <th style={thStyle}>Update Title</th>
              <th style={thStyle}>Update Message</th>
              <th style={thStyle}>Store URL</th>
              <th style={thStyle}>Created At</th>
              <th style={thStyle}>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(configData) ? (
              configData.map((item, index) => (
                <tr key={index}>
                  <td style={tdStyle}>{item.platform === "android" ? "Android" : "iOS"}</td>
                  <td style={tdStyle}>{item.latest_version || "-"}</td>
                  <td style={tdStyle}>{item.minimum_supported_version || "-"}</td>
                  <td style={tdStyle}>{item.force_update ? "Yes" : "No"}</td>
                  <td style={tdStyle}>{item.update_title || "-"}</td>
                  <td style={tdStyle}>{item.update_message || "-"}</td>
                  <td style={tdStyle}>{item.store_url || "-"}</td>
                  <td style={tdStyle}>{formatDate(item.created_at)}</td>
                  <td style={tdStyle}>{formatDate(item.updated_at)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td style={tdStyle}>{configData.platform === "android" ? "Android" : "iOS"}</td>
                <td style={tdStyle}>{configData.latest_version || "-"}</td>
                <td style={tdStyle}>{configData.minimum_supported_version || "-"}</td>
                <td style={tdStyle}>{configData.force_update ? "Yes" : "No"}</td>
                <td style={tdStyle}>{configData.update_title || "-"}</td>
                <td style={tdStyle}>{configData.update_message || "-"}</td>
                <td style={tdStyle}>{configData.store_url || "-"}</td>
                <td style={tdStyle}>{formatDate(configData.created_at)}</td>
                <td style={tdStyle}>{formatDate(configData.updated_at)}</td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <Typography color="text.secondary">
          No configuration found for {platform}.
        </Typography>
      )}
    </Box>
  );
};

export default AppConfigTable;
